import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { Subtitles, pubnub, TextToSpeech, SpeechToText, MainWrapper, Select } from 'wwtc-sdk';
import {
    endpointSpeechToText,
    endpointTextToSpeech,
    endpointTranslate,
    endpointDetectLanguage,
    endpointUploadSubtitlesPresigned,
    signedEndpoint,
} from '../constants/connection';
import specialLanguages from '../constants/specialLanguages';
import { Download, Times, Users, User, LanguageDetection, Share, LogoutIcon } from './svg/icons';
import buildSubtitlesFile, { buildSubtitlesTxtContent } from '../utils/subtitles';
import { uploadSubtitlesToS3 } from '../utils/uploadSubtitles';
import { handlePreRequestSubtitles } from './utils';
import notifications from '../notifications';
import { createModeratorWaitingRoomController } from '../waitingRoom';
import { buildParticipantInvitationLink } from './participantInvitation';

function Main() {
    // This values come from Login components
    // Also could set up constants values
    // This values are required like props of the MainWrapper
    const history = useHistory();
    const [modalParticipants, setModalParticipants] = useState(false);
    const [waitingRoomRequests, setWaitingRoomRequests] = useState([]);
    const [waitingRoomModalVisible, setWaitingRoomModalVisible] = useState(false);
    const [modalLanguageDetection, setModalLanguageDetection] = useState(false);
    const [modalClipboard, setModalClipboard] = useState(false);
    const activeLanguageDetection = window.activeLanguageDetection;
    const activeWaitingRoom = window.activeWaitingRoom;
    const subtitlesRef = useRef(null);
    const waitingRoomControllerRef = useRef(null);
    const previousWaitingCountRef = useRef(0);

    const {
        userName,
        language,
        gender,
        roomName,
        apiToken,
        logoHeader,
        languagesPreTranslate,
        languages,
    } = history.location.state || {
        userName: '',
        language: '',
        gender: '',
        roomName: 'default', // default name
        apiToken: null,
        logoHeader: null,
        languagesPreTranslate: null,
        languages: [],
    };

    useEffect(() => {
        if (userName === '') {
            history.push(`/`, {
                userName: userName,
                language: language,
                gender: gender,
            });
        }

        if (!activeWaitingRoom) {
            setWaitingRoomRequests([]);
            setWaitingRoomModalVisible(false);
            previousWaitingCountRef.current = 0;
            return undefined;
        }

        const controller = createModeratorWaitingRoomController({
            client: pubnub,
            room: roomName,
            onQueueChange: setWaitingRoomRequests,
        });
        waitingRoomControllerRef.current = controller;
        controller.connect();

        return () => {
            controller.disconnect();
            waitingRoomControllerRef.current = null;
        };
    }, [activeWaitingRoom, gender, history, language, roomName, userName]);

    useEffect(() => {
        const previousCount = previousWaitingCountRef.current;
        const nextCount = waitingRoomRequests.length;

        if (nextCount === 0) {
            setWaitingRoomModalVisible(false);
        } else if (previousCount === 0) {
            setWaitingRoomModalVisible(true);
        }

        previousWaitingCountRef.current = nextCount;
    }, [waitingRoomRequests.length]);

    const resolveWaitingRoomRequest = async (request, resolution) => {
        try {
            if (resolution === 'admit') {
                await waitingRoomControllerRef.current?.admit(request);
            } else {
                await waitingRoomControllerRef.current?.deny(request);
            }
        } catch (error) {
            notifications.error({
                title: `Unable to ${resolution} participant`,
                description: error?.message,
            });
            throw error;
        }
    };

    return (
        <MainWrapper
            autologin={false}
            gender={gender}
            language={language}
            roomName={roomName}
            userName={userName}
            languages={languages}
            notifications={notifications}
        >
            <Header
                logoHeader={logoHeader}
                pubnub={pubnub}
                roomName={roomName}
                apiToken={apiToken}
                setModalParticipants={setModalParticipants}
                setModalLanguageDetection={setModalLanguageDetection}
                setModalClipboard={setModalClipboard}
                activeLanguageDetection={activeLanguageDetection}
                subtitlesRef={subtitlesRef}
                waitingRoomCount={waitingRoomRequests.length}
                openWaitingRoom={() => setWaitingRoomModalVisible(true)}
            />
            <WrapperComponents
                apiToken={apiToken}
                setModalParticipants={setModalParticipants}
                modalParticipants={modalParticipants}
                waitingRoomRequest={waitingRoomRequests[0] || null}
                waitingRoomCount={waitingRoomRequests.length}
                waitingRoomModalVisible={waitingRoomModalVisible}
                closeWaitingRoom={() => setWaitingRoomModalVisible(false)}
                resolveWaitingRoomRequest={resolveWaitingRoomRequest}
                setModalLanguageDetection={setModalLanguageDetection}
                modalLanguageDetection={modalLanguageDetection}
                modalClipboard={modalClipboard}
                setModalClipboard={setModalClipboard}
                languagesPreTranslate={languagesPreTranslate}
                languages={languages}
                subtitlesRef={subtitlesRef}
            />
        </MainWrapper>
    );
}

function Header(props) {
    // initName comes from autologin component
    const {
        logoHeader,
        pubnub,
        initName,
        roomName,
        apiToken,
        setModalParticipants,
        setModalLanguageDetection,
        setModalClipboard,
        activeLanguageDetection,
        subtitlesRef,
        waitingRoomCount,
        openWaitingRoom,
    } = props;

    // Store references to buttons for focus management
    const shareButtonRef = useRef(null);
    const participantsButtonRef = useRef(null);
    const languageDetectionButtonRef = useRef(null);
    const waitingRoomButtonRef = useRef(null);
    const [isUploadingSubtitles, setIsUploadingSubtitles] = useState(false);

    const handleExportSubtitles = async (subtitles) => {
        const contentTxt = buildSubtitlesTxtContent(subtitles);
        const blob = new Blob([contentTxt], { type: 'text/plain;charset=utf-8' });
        const filename = `nowtalk-${roomName}-${new Date().toISOString().slice(0, 10)}.txt`;

        setIsUploadingSubtitles(true);
        try {
            const result = await uploadSubtitlesToS3(
                blob,
                filename,
                endpointUploadSubtitlesPresigned,
            );
            if (result.success) {
                console.log('Audit log exported successfully');
            } else {
                console.log(`Error exporting audit log: ${result.error}`);
            }
        } finally {
            setIsUploadingSubtitles(false);
        }
    };

    const handleLogout = async () => {
        // export audit log
        if (window.enableAudit) {
            const subtitles = subtitlesRef.current.state.subtitles;
            console.log('subtitles', subtitles);
            await handleExportSubtitles(subtitles);
        }
        if (pubnub) {
            await pubnub.signal({
                message: 'logout_admin',
                channel: roomName,
            });
            pubnub.unsubscribeAll();
            window.location.replace(window.location.origin);
        } else {
            window.location.replace(window.location.origin);
        }
    };

    const handleKeyPress = (event, callback) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            callback();
        }
    };

    const handleShareClick = () => {
        // Store the current focused element (the button that triggered the modal)
        const activeElement = document.activeElement;
        if (activeElement) {
            sessionStorage.setItem(
                'modalTriggerElementId',
                activeElement.getAttribute('data-modal-trigger') || 'share-btn',
            );
        }
        setModalClipboard(true);
        notifications.info({
            title: 'Share room opened',
            description: 'Participant sharing options are ready.',
        });
    };

    const handleParticipantsClick = () => {
        const activeElement = document.activeElement;
        if (activeElement) {
            sessionStorage.setItem(
                'modalTriggerElementId',
                activeElement.getAttribute('data-modal-trigger') || 'participants-btn',
            );
        }
        setModalParticipants(true);
    };

    const handleLanguageDetectionClick = () => {
        const activeElement = document.activeElement;
        if (activeElement) {
            sessionStorage.setItem(
                'modalTriggerElementId',
                activeElement.getAttribute('data-modal-trigger') || 'lang-detection-btn',
            );
        }
        setModalLanguageDetection(true);
    };

    const handleWaitingRoomClick = () => {
        if (waitingRoomCount > 0) openWaitingRoom();
    };

    return (
        <header role="banner">
            <div className="header-left-section">
                <div className="info-user">
                    <img src={`${logoHeader}`} alt="WWTC" />
                </div>
            </div>
            <div className="header-center-section">
                <div className="direct-buttons hidden-mobile">
                    <div
                        role="heading"
                        aria-level="1"
                        tabIndex="0"
                        aria-label="Application: NOW Talk and Text - Moderator Dashboard"
                    >
                        <span>NOW Talk and Text</span>
                    </div>
                </div>
            </div>
            <div className="header-right-section">
                <div className="header-actions">
                    {activeLanguageDetection && (
                        <div className="action-group">
                            <button
                                ref={languageDetectionButtonRef}
                                type="button"
                                className="lang-detection-btn"
                                data-modal-trigger="lang-detection-btn"
                                data-toggle="tooltip"
                                data-placement="bottom"
                                title="Language Detection"
                                aria-label="Language Detection"
                                aria-describedby="lang-detection-tooltip"
                                data-container="body"
                                tabIndex="0"
                                onClick={handleLanguageDetectionClick}
                                onKeyDown={(e) => handleKeyPress(e, handleLanguageDetectionClick)}
                            >
                                <LanguageDetection />
                                <span className="visually-hidden">Language Detection</span>
                            </button>
                        </div>
                    )}
                    <div className="action-group share-group">
                        <button
                            ref={shareButtonRef}
                            type="button"
                            className="share-btn"
                            data-modal-trigger="share-btn"
                            data-toggle="tooltip"
                            data-placement="bottom"
                            title="Share room"
                            aria-label="Share room"
                            aria-describedby="share-room-tooltip"
                            data-container="body"
                            tabIndex="0"
                            onClick={handleShareClick}
                            onKeyDown={(e) => handleKeyPress(e, handleShareClick)}
                        >
                            <span className="visually-hidden">Share room</span>
                            <Share />
                        </button>
                    </div>
                    {waitingRoomCount > 0 && (
                        <div className="action-group waiting-room-group">
                            <button
                                ref={waitingRoomButtonRef}
                                type="button"
                                className="waiting-room-btn"
                                title={`${waitingRoomCount} waiting for admission`}
                                aria-label={`Open waiting room. ${waitingRoomCount} waiting`}
                                onClick={handleWaitingRoomClick}
                                onKeyDown={(e) => handleKeyPress(e, handleWaitingRoomClick)}
                            >
                                <span className="waiting-room-label hidden-mobile">Waiting room</span>
                                <span className="waiting-room-badge" aria-hidden="true">
                                    {waitingRoomCount}
                                </span>
                            </button>
                        </div>
                    )}
                    <div className="action-group participants-group">
                        <span className="participants-label hidden-mobile">Roll Call</span>
                        <button
                            ref={participantsButtonRef}
                            type="button"
                            className="participants-btn"
                            data-modal-trigger="participants-btn"
                            data-toggle="tooltip"
                            data-placement="bottom"
                            title="Participants"
                            aria-label="View participants"
                            aria-describedby="participants-tooltip"
                            data-container="body"
                            tabIndex="0"
                            onClick={handleParticipantsClick}
                            onKeyDown={(e) => handleKeyPress(e, handleParticipantsClick)}
                        >
                            <Users />
                        </button>
                    </div>
                    <div className="action-group user-group">
                        <span className="profile-name hidden-mobile">{initName}</span>
                        <button
                            type="button"
                            disabled={isUploadingSubtitles}
                            className={
                                isUploadingSubtitles ? 'logout-btn Button-disabled' : 'logout-btn'
                            }
                            data-toggle="tooltip"
                            data-placement="bottom"
                            title="Logout"
                            aria-label="Logout"
                            aria-describedby="logout-tooltip"
                            data-container="body"
                            aria-hidden="false"
                            tabIndex="0"
                            onClick={handleLogout}
                            onKeyDown={(e) => handleKeyPress(e, handleLogout)}
                        >
                            <LogoutIcon />
                        </button>
                    </div>
                </div>
            </div>
        </header>
    );
}

function WrapperComponents(props) {
    // values from login
    const {
        apiToken,
        initName,
        initLanguage,
        initGender,
        conferenceName,
        modalParticipants,
        setModalParticipants,
        waitingRoomRequest,
        waitingRoomCount,
        waitingRoomModalVisible,
        closeWaitingRoom,
        resolveWaitingRoomRequest,
        modalLanguageDetection,
        setModalLanguageDetection,
        modalClipboard,
        setModalClipboard,
        languagesPreTranslate,
        languages,
        subtitlesRef,
    } = props;
    const [isBuildingPdf, setIsBuildingPdf] = useState(false);
    const [modalDownload, setModalDownload] = useState(false);
    const [languageDetectionEnabled, setLanguageDetectionEnabled] = useState(false);
    const [valueLanguageDetection, setValueLanguageDetection] = useState(null);
    const canKickParticipants = window.canKickParticipants;

    const isSpecialLanguage = (subtitles) => {
        const specialSubtitle = specialLanguages.find((special) => {
            var regex = new RegExp(special, 'g');
            var atLeastOneMatches = subtitles.some((e) => regex.test(e.speakerLanguage));
            return atLeastOneMatches;
        });

        return !!specialSubtitle;
    };

    const downloadSubtitlesPDF = (subtitles) => {
        setIsBuildingPdf(true);
        const contentPDF = buildSubtitlesFile(subtitles);

        // detect special languages
        const subtitlesWithSpecialLanguages = isSpecialLanguage(subtitles);

        // download unicode typography
        if (subtitlesWithSpecialLanguages) {
            window.print();
            setIsBuildingPdf(false);
            return;
        }

        var docDefinition = {
            content: contentPDF,
            defaultStyle: {
                font: 'Roboto',
                fontSize: 12,
            },
        };

        window.pdfMake.createPdf(docDefinition).download(() => {
            setIsBuildingPdf(false);
        });
    };

    const fetchTextToSpeechCustom = async (endpoint, text, sourceLanguage, voice, itemTtsCall) => {
        try {
            let response = await fetch(endpoint.url.replace(/:sourceLanguage/g, sourceLanguage), {
                method: endpoint.method,
                redirect: 'follow',
                body: JSON.stringify({
                    text,
                }),
            });
            let data = await response.json();
            try {
                var arrayBuffer = _base64ToArrayBuffer(data.audio);
                let blob = new Blob([arrayBuffer], { type: 'audio/wav' });
                const downloadUrl = window.webkitURL.createObjectURL(blob);
                return { downloadUrl, indexCall: itemTtsCall };
            } catch (e) {
                return {
                    indexCall: itemTtsCall,
                    error: e,
                };
            }
        } catch (err) {
            return {
                indexCall: itemTtsCall,
                error: err,
            };
        }
    };

    const _base64ToArrayBuffer = (base64) => {
        var binary_string = window.atob(base64);
        var len = binary_string.length;
        var bytes = new Uint8Array(len);
        for (var i = 0; i < len; i++) {
            bytes[i] = binary_string.charCodeAt(i);
        }
        return bytes.buffer;
    };

    const kickOutParticipant = async (participant) => {
        await pubnub.signal({
            message: `kickout_participant-${participant.id}`,
            channel: conferenceName,
        });
    };

    const deleteParticipantsContent = async () => {
        try {
            await pubnub.signal({
                message: 'clean_history_content',
                channel: conferenceName,
            });
            notifications.success({
                title: 'Subtitles cleared',
                description: 'All subtitles were removed from this session.',
            });
        } catch (error) {
            notifications.error({
                title: 'Delete subtitles failed',
                description: error?.message,
            });
        }
    };

    return (
        <main role="main" className="wrapper-components">
            <Subtitles.SubtitlesUI
                username={initName}
                initialSourceLanguage={initLanguage}
                initialTargetLanguage={initLanguage}
                initialVisible={true}
                defaultBilingual={false}
                endpoint={signedEndpoint(apiToken, endpointTranslate)}
                provider="pubnub"
                speechRecognition={true}
                showButtonCloseComponent={false}
                showFlipButton={true}
                showSpeedOption={true}
                showLanguageServices={true}
                style={stylesWrapper.fullContainer}
                ttsOption={true}
                channel={conferenceName}
                languagesPreTranslate={languagesPreTranslate}
                render={(subtitles) =>
                    isBuildingPdf ? null : (
                        <button
                            className="a-element"
                            onClick={() => downloadSubtitlesPDF(subtitles)}
                            title="Download subtitles"
                            aria-label="Download subtitles"
                            aria-describedby="download-subtitles-tooltip"
                        >
                            <span className="visually-hidden">Download subtitles</span>
                            <Download />
                        </button>
                    )
                }
                preRequestSubtitles={handlePreRequestSubtitles}
                requestSameLanguage={window.profanityFilterEnabled}
                ref={subtitlesRef}
                bilingualTextColor={window.bilingualTextColor}
            />
            <SpeechToText.SpeechToTextUI
                username={initName}
                initialLanguage={initLanguage}
                initialVisible={false}
                initialActive={false}
                withUI={false} // to use only speech recognition
                endpoint={signedEndpoint(apiToken, endpointSpeechToText)}
                detectLanguage={languageDetectionEnabled}
                endpointDetectLanguage={signedEndpoint(apiToken, endpointDetectLanguage)}
                paramsDetectLanguage={[initLanguage, valueLanguageDetection?.value]}
            />
            <TextToSpeech.TextToSpeechUI
                initialLanguage={initLanguage}
                initialGender={initGender}
                initialVisible={false}
                initialActive={false}
                withUI={false} // to use only audio player
                endpoint={signedEndpoint(apiToken, endpointTextToSpeech)}
                fetchCustom={fetchTextToSpeechCustom}
            />
            {modalParticipants ? (
                <ModalParticipants
                    closeModal={() => setModalParticipants(false)}
                    pubnub={pubnub}
                    conferenceName={conferenceName}
                    kickOutParticipant={kickOutParticipant}
                    deleteParticipantsContent={deleteParticipantsContent}
                    canKickParticipants={canKickParticipants}
                />
            ) : null}
            {waitingRoomModalVisible && waitingRoomRequest ? (
                <ModalJoinRoom
                    closeModal={closeWaitingRoom}
                    waitingRoomRequest={waitingRoomRequest}
                    waitingRoomCount={waitingRoomCount}
                    resolveWaitingRoomRequest={resolveWaitingRoomRequest}
                />
            ) : null}
            {modalLanguageDetection ? (
                <ModalLanguageDetection
                    closeModal={() => setModalLanguageDetection(false)}
                    languages={languages}
                    languageDetectionEnabled={languageDetectionEnabled}
                    setLanguageDetectionEnabled={setLanguageDetectionEnabled}
                    valueLanguageDetection={valueLanguageDetection}
                    setValueLanguageDetection={setValueLanguageDetection}
                />
            ) : null}
            {modalClipboard ? (
                <ModalClipboard closeModal={() => setModalClipboard(false)} />
            ) : null}
            {isBuildingPdf ? <ModalDownload onCloseModal={() => setModalDownload(false)} /> : null}
        </main>
    );
}

const ModalDownload = ({ onCloseModal }) => {
    return (
        <div style={stylesWrapper.containerModal}>
            <div style={{ ...stylesWrapper.wrapperModal, height: 180, width: 450 }}>
                <div className="header-container">
                    <div className="header-title">
                        <span>PDF Subtitles</span>
                    </div>
                    <div className="header-options"></div>
                </div>
                <div
                    className="body-container"
                    style={{ ...stylesWrapper.bodyModal, textAlign: 'center' }}
                >
                    <p>Downloading PDF file...</p>
                    <p style={{ fontSize: 14 }}>Please wait, this could take a few seconds.</p>
                </div>
            </div>
        </div>
    );
};

const ModalParticipants = (props) => {
    const {
        closeModal,
        pubnub,
        conferenceName,
        kickOutParticipant,
        deleteParticipantsContent,
        canKickParticipants,
    } = props;
    const [participants, setParticipants] = useState([]);
    const myPubnubId = pubnub.userId;
    const modalRef = useRef(null);

    const handleClose = () => {
        closeModal();
        // Return focus to the element that opened the modal
        setTimeout(() => {
            const triggerElementId = sessionStorage.getItem('modalTriggerElementId');
            if (triggerElementId) {
                const triggerElement = document.querySelector(
                    `[data-modal-trigger="${triggerElementId}"]`,
                );
                if (triggerElement && typeof triggerElement.focus === 'function') {
                    triggerElement.focus();
                    console.log('Focus returned to:', triggerElementId); // Debug log
                } else {
                    console.log('Could not find trigger element:', triggerElementId); // Debug log
                }
                sessionStorage.removeItem('modalTriggerElementId');
            }
        }, 150);
    };

    useEffect(() => {
        // Focus on the title first
        const timer = setTimeout(() => {
            const title = modalRef.current?.querySelector('#participants-title');
            if (title) {
                title.focus();
            }
        }, 100);

        // Handle Escape key and focus trap
        const handleKeydown = (event) => {
            if (event.key === 'Escape') {
                handleClose();
                return;
            }

            // Focus trap logic with custom order
            if (event.key === 'Tab') {
                event.preventDefault();

                // Define the correct tab order manually
                const orderedSelectors = [
                    '#participants-title',
                    'div[aria-live="polite"]',
                    'li.participant-item',
                    'button[aria-label="Download participants list"]',
                    'button.btn-success', // Include even if disabled
                    'button[aria-label="Close participants dialog"]',
                ];

                let allElements = [];
                orderedSelectors.forEach((selector) => {
                    const elements = modalRef.current?.querySelectorAll(selector);
                    if (elements) {
                        allElements.push(...Array.from(elements));
                    }
                });

                const currentIndex = allElements.indexOf(document.activeElement);
                let nextIndex;

                if (event.shiftKey) {
                    // Shift+Tab (backward)
                    nextIndex = currentIndex <= 0 ? allElements.length - 1 : currentIndex - 1;
                } else {
                    // Tab (forward)
                    nextIndex = currentIndex >= allElements.length - 1 ? 0 : currentIndex + 1;
                }

                // Skip disabled elements
                let attempts = 0;
                while (
                    allElements[nextIndex] &&
                    allElements[nextIndex].disabled &&
                    attempts < allElements.length
                ) {
                    if (event.shiftKey) {
                        nextIndex = nextIndex <= 0 ? allElements.length - 1 : nextIndex - 1;
                    } else {
                        nextIndex = nextIndex >= allElements.length - 1 ? 0 : nextIndex + 1;
                    }
                    attempts++;
                }

                if (allElements[nextIndex] && !allElements[nextIndex].disabled) {
                    allElements[nextIndex].focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeydown);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('keydown', handleKeydown);
        };
    }, []);

    useEffect(() => {
        pubnub
            .hereNow({
                channels: [conferenceName],
                includeState: true,
            })
            .then(async (res) => {
                let participants = [];
                const participantsInChannel = res.channels[conferenceName].occupants;

                for (let index = 0; index < participantsInChannel.length; index++) {
                    const item = participantsInChannel[index];

                    try {
                        const { data } = await pubnub.objects.getUUIDMetadata({
                            uuid: item.uuid,
                        });
                        participants.push(data);
                    } catch (e) {
                        console.log(e);
                    }
                }
                setParticipants(participants);
            });
    }, []);

    const handleParticipantKickOut = (participant) => {
        kickOutParticipant(participant);
        const filteredParticipants = participants.filter((e) => e.id !== participant.id);

        setParticipants(filteredParticipants);
    };

    const downloadParticipants = () => {
        var contentPDF = [];
        contentPDF.push({
            text: `${conferenceName} - Roll Call`,
            style: 'header',
            bold: true,
        });
        participants.forEach((item, index) => {
            contentPDF.push({
                ul: [{ text: item.name, bold: false }],
            });
        });

        var docDefinition = {
            content: contentPDF,
            defaultStyle: {
                fontSize: 12,
            },
        };
        window.pdfMake.createPdf(docDefinition).download('rollCallWWTC.pdf');
    };

    return (
        <div
            style={stylesWrapper.containerModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="participants-title"
            ref={modalRef}
            tabIndex="-1"
        >
            <div style={stylesWrapper.wrapperModal}>
                <div className="header-container">
                    <div className="header-title">
                        <h2
                            id="participants-title"
                            tabIndex="0"
                            style={{ margin: 0, fontSize: 'inherit' }}
                        >
                            Roll Call
                        </h2>
                    </div>
                    <div className="header-options">
                        <button
                            className="a-element"
                            onClick={downloadParticipants}
                            aria-label="Download participants list"
                            aria-describedby="download-participants-tooltip"
                            tabIndex="0"
                        >
                            <Download />
                        </button>
                        <button
                            className="a-element"
                            onClick={handleClose}
                            aria-label="Close participants dialog"
                            aria-describedby="close-participants-tooltip"
                            tabIndex="0"
                        >
                            <Times />
                        </button>
                    </div>
                </div>
                <div className="body-container" style={stylesWrapper.bodyModal} role="document">
                    <div tabIndex="0" aria-live="polite" style={{ marginBottom: 10 }}>
                        <p>Participants in this session ({participants.length}):</p>
                    </div>
                    <ul aria-label="List of participants">
                        {participants.map((item, index) => {
                            return (
                                <li
                                    key={index}
                                    style={stylesWrapper.participantItem}
                                    className="participant-item"
                                    tabIndex="0"
                                    aria-label={`Participant: ${item.name}${
                                        myPubnubId === item.id ? ' (You)' : ''
                                    }`}
                                >
                                    <span style={stylesWrapper.descriptionParticipant}>
                                        <span aria-hidden="true">
                                            <User />
                                        </span>
                                        <span style={{ marginLeft: 8 }}>
                                            {item.name}
                                            {myPubnubId === item.id ? ' (You)' : null}
                                        </span>
                                    </span>
                                    {myPubnubId !== item.id && canKickParticipants ? (
                                        <button
                                            className="a-element"
                                            onClick={() => handleParticipantKickOut(item)}
                                            aria-label={`Remove ${item.name} from session`}
                                            aria-describedby="remove-participant-tooltip"
                                            tabIndex="0"
                                        >
                                            <Times />
                                        </button>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <hr />
                <div
                    className="footer-container d-flex justify-content-center align-items-center"
                    styles={stylesWrapper.footerModal}
                >
                    <button
                        className=" btn btn-success btn-sm"
                        disabled={participants.length === 1}
                        onClick={deleteParticipantsContent}
                        tabIndex="0"
                        aria-label="Delete all participants' chat content and subtitles from this session"
                        aria-describedby="delete-content-tooltip"
                    >
                        Delete participants content
                    </button>
                </div>
            </div>
        </div>
    );
};

const ModalJoinRoom = (props) => {
    const { closeModal, waitingRoomRequest, waitingRoomCount, resolveWaitingRoomRequest } = props;
    const { requestId, displayName } = waitingRoomRequest;
    const [resolving, setResolving] = useState(null);

    useEffect(() => setResolving(null), [requestId]);

    const handleResolution = async (resolution) => {
        setResolving(resolution);
        try {
            await resolveWaitingRoomRequest(waitingRoomRequest, resolution);
        } catch (error) {
            setResolving(null);
        }
    };

    return (
        <div
            style={stylesWrapper.containerModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="join-room-title"
        >
            <div style={{ ...stylesWrapper.wrapperModal, height: 230 }}>
                <div className="header-container">
                    <div className="header-title">
                        <span id="join-room-title">Join participant</span>
                    </div>
                    <div className="header-options">
                        <button
                            className="a-element"
                            onClick={closeModal}
                            aria-label="Close waiting room"
                        >
                            <Times />
                        </button>
                    </div>
                </div>
                <div className="body-container text-center" style={stylesWrapper.bodyModal}>
                    <p>
                        {' '}
                        <strong>{displayName}</strong> would like to join the room
                    </p>
                    <p aria-live="polite">
                        {waitingRoomCount}{' '}
                        {waitingRoomCount === 1 ? 'person is' : 'people are'} waiting
                    </p>
                </div>
                <div
                    className="footer-container d-flex justify-content-center align-items-center"
                    styles={stylesWrapper.footerModal}
                >
                    <button
                        className="btn btn-deny"
                        disabled={!!resolving}
                        onClick={() => handleResolution('deny')}
                        style={{ marginRight: 12 }}
                    >
                        {resolving === 'deny' ? 'Denying...' : 'Deny'}
                    </button>
                    <button
                        className="btn btn-primary"
                        disabled={!!resolving}
                        onClick={() => handleResolution('admit')}
                    >
                        {resolving === 'admit' ? 'Admitting...' : 'Admit to Session'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ModalLanguageDetection = (props) => {
    const {
        closeModal,
        languages,
        languageDetectionEnabled,
        setLanguageDetectionEnabled,
        valueLanguageDetection,
        setValueLanguageDetection,
    } = props;

    const modalRef = useRef(null);

    const handleOkModal = () => {
        if (!!languageDetectionEnabled && !valueLanguageDetection) {
            alert('Please, select your secondary language');
            return;
        }

        if (!languageDetectionEnabled) {
            setValueLanguageDetection(null);
            closeModal();
        }

        if (!!languageDetectionEnabled && !!valueLanguageDetection) {
            closeModal();
        }
    };

    const handleClose = () => {
        if (!!languageDetectionEnabled && !valueLanguageDetection) {
            setValueLanguageDetection(null);
            setLanguageDetectionEnabled(false);
        }

        if (!languageDetectionEnabled) {
            setValueLanguageDetection(null);
        }

        if (!!languageDetectionEnabled && !!valueLanguageDetection) {
            // Keep current state
        }

        closeModal();

        // Return focus to the element that opened the modal
        setTimeout(() => {
            const triggerElementId = sessionStorage.getItem('modalTriggerElementId');
            if (triggerElementId) {
                const triggerElement = document.querySelector(
                    `[data-modal-trigger="${triggerElementId}"]`,
                );
                if (triggerElement && typeof triggerElement.focus === 'function') {
                    triggerElement.focus();
                }
                sessionStorage.removeItem('modalTriggerElementId');
            }
        }, 150);
    };

    useEffect(() => {
        // Focus on the title first
        const timer = setTimeout(() => {
            const title = modalRef.current?.querySelector('#language-detection-title');
            if (title) {
                title.focus();
            }
        }, 100);

        // Handle Escape key and focus trap
        const handleKeydown = (event) => {
            if (event.key === 'Escape') {
                handleClose();
                return;
            }

            // Focus trap logic with custom order
            if (event.key === 'Tab') {
                event.preventDefault();

                // Define the correct tab order manually
                const orderedSelectors = [
                    '#language-detection-title',
                    'input[type="radio"]',
                    '.css-1pahdxg-control', // Select component
                    'button.btn-success',
                    'button[aria-label="Close language detection dialog"]',
                ];

                let allElements = [];
                orderedSelectors.forEach((selector) => {
                    const elements = modalRef.current?.querySelectorAll(selector);
                    if (elements) {
                        allElements.push(...Array.from(elements));
                    }
                });

                const currentIndex = allElements.indexOf(document.activeElement);
                let nextIndex;

                if (event.shiftKey) {
                    // Shift+Tab (backward)
                    nextIndex = currentIndex <= 0 ? allElements.length - 1 : currentIndex - 1;
                } else {
                    // Tab (forward)
                    nextIndex = currentIndex >= allElements.length - 1 ? 0 : currentIndex + 1;
                }

                if (allElements[nextIndex]) {
                    allElements[nextIndex].focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeydown);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('keydown', handleKeydown);
        };
    }, []);

    return (
        <div
            style={stylesWrapper.containerModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="language-detection-title"
            ref={modalRef}
            tabIndex="-1"
        >
            <div style={{ ...stylesWrapper.wrapperModal, height: 250 }}>
                <div className="header-container">
                    <div className="header-title">
                        <h2
                            id="language-detection-title"
                            tabIndex="0"
                            style={{ margin: 0, fontSize: 14 }}
                        >
                            Language Detection
                        </h2>
                    </div>
                    <div className="header-options">
                        <button
                            className="a-element"
                            onClick={handleClose}
                            aria-label="Close language detection dialog"
                            aria-describedby="close-lang-detection-tooltip"
                            tabIndex="0"
                        >
                            <Times />
                        </button>
                    </div>
                </div>
                <div className="body-container" style={stylesWrapper.bodyModal} role="document">
                    <div tabIndex="0" style={{ marginBottom: 15 }}>
                        <p style={{ fontSize: 14, textAlign: 'center' }}>
                            Configure automatic language detection for speech recognition
                        </p>
                    </div>
                    <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                        <legend className="visually-hidden">Language detection options</legend>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-evenly',
                                alignItems: 'center',
                            }}
                        >
                            <div>
                                <input
                                    type="radio"
                                    id="disabled"
                                    name="languageDetectionEnabled"
                                    value="disabled"
                                    checked={languageDetectionEnabled === false}
                                    onChange={(e) => setLanguageDetectionEnabled(false)}
                                    tabIndex="0"
                                />
                                <label htmlFor="disabled" style={{ fontSize: 14, marginLeft: 5 }}>
                                    Disabled
                                </label>
                            </div>

                            <div>
                                <input
                                    type="radio"
                                    id="enabled"
                                    name="languageDetectionEnabled"
                                    value="enabled"
                                    checked={languageDetectionEnabled === true}
                                    onChange={(e) => setLanguageDetectionEnabled(true)}
                                    tabIndex="0"
                                />
                                <label htmlFor="enabled" style={{ fontSize: 14, marginLeft: 5 }}>
                                    Enabled
                                </label>
                            </div>
                        </div>
                    </fieldset>
                    {languageDetectionEnabled && (
                        <div style={{ marginTop: 20 }}>
                            <label
                                htmlFor="language-select"
                                style={{ fontSize: 14, display: 'block', marginBottom: 8 }}
                            >
                                Select your secondary language
                            </label>
                            <Select
                                id="language-select"
                                handleChange={(lng) => setValueLanguageDetection(lng)}
                                placeholder="Select language"
                                data={languages}
                                value={valueLanguageDetection || ''}
                                showLanguageServices={true}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                aria-label="Select secondary language for detection"
                            />
                        </div>
                    )}
                </div>
                <hr />
                <div
                    className="footer-container d-flex justify-content-center align-items-center"
                    styles={stylesWrapper.footerModal}
                >
                    <button
                        onClick={handleOkModal}
                        className="btn btn-success btn-sm"
                        tabIndex="0"
                        aria-label="Apply language detection settings"
                        aria-describedby="apply-lang-detection-tooltip"
                    >
                        Ok
                    </button>
                </div>
            </div>
        </div>
    );
};

export const ModalClipboard = (props) => {
    const { closeModal } = props;
    const [isCopied, setIsCopied] = useState(false);
    const [isQrCopied, setIsQrCopied] = useState(false);
    const modalRef = useRef(null);

    const handleClose = () => {
        closeModal();
        // Return focus to the element that opened the modal
        setTimeout(() => {
            const triggerElementId = sessionStorage.getItem('modalTriggerElementId');
            if (triggerElementId) {
                const triggerElement = document.querySelector(
                    `[data-modal-trigger="${triggerElementId}"]`,
                );
                if (triggerElement && typeof triggerElement.focus === 'function') {
                    triggerElement.focus();
                    console.log('Focus returned to:', triggerElementId); // Debug log
                } else {
                    console.log('Could not find trigger element:', triggerElementId); // Debug log
                }
                sessionStorage.removeItem('modalTriggerElementId');
            }
        }, 150);
    };

    useEffect(() => {
        // Focus on the first focusable element and setup screen reader
        const timer = setTimeout(() => {
            const firstFocusable = modalRef.current?.querySelector('#share-room-title');
            if (firstFocusable) {
                firstFocusable.focus();
            }

            // Force screen reader to read the content
            const description = document.getElementById('share-room-description');
            if (description) {
                description.setAttribute('aria-live', 'polite');
                description.setAttribute('aria-atomic', 'true');
            }
        }, 100);

        // Handle Escape key and focus trap
        const handleKeydown = (event) => {
            if (event.key === 'Escape') {
                handleClose();
                return;
            }

            // Focus trap logic with custom order
            if (event.key === 'Tab') {
                event.preventDefault();

                // Define the correct tab order manually
                const orderedSelectors = [
                    '#share-room-title',
                    '#share-room-description',
                    '#qr-share-room',
                    '.Button-primary',
                    '.Button-secondary',
                    'button[aria-label*="Close"]',
                ];

                let allElements = [];
                orderedSelectors.forEach((selector) => {
                    const elements = modalRef.current?.querySelectorAll(selector);
                    if (elements) {
                        allElements.push(...Array.from(elements));
                    }
                });

                const currentIndex = allElements.indexOf(document.activeElement);
                let nextIndex;

                if (event.shiftKey) {
                    // Shift+Tab (backward)
                    nextIndex = currentIndex <= 0 ? allElements.length - 1 : currentIndex - 1;
                } else {
                    // Tab (forward)
                    nextIndex = currentIndex >= allElements.length - 1 ? 0 : currentIndex + 1;
                }

                if (allElements[nextIndex]) {
                    allElements[nextIndex].focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeydown);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('keydown', handleKeydown);
        };
    }, []);

    const link = buildParticipantInvitationLink();

    const copyLinkToClipboard = () => {
        try {
            // Create a "hidden" input
            var aux = document.createElement('input');
            // Assign it the value of the specified element
            aux.setAttribute('value', link);

            // Append it to the body
            document.body.appendChild(aux);

            // Highlight its content
            aux.select();

            // Copy the highlighted text
            const copied = document.execCommand('copy');

            // Remove it from the body
            document.body.removeChild(aux);

            if (!copied) {
                throw new Error('Clipboard copy failed');
            }

            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
            notifications.success({
                title: 'Share link copied',
                description: 'Participant link copied to the clipboard.',
            });
        } catch (error) {
            notifications.error({
                title: 'Share link copy failed',
                description: error?.message,
            });
        }
    };

    const copyQrToClipboard = (elementId) => {
        const canvas = document.getElementById(elementId);
        if (!canvas) {
            notifications.error({ title: 'QR copy failed', description: 'QR image not found' });
            return;
        }

        canvas.toBlob(async function (blob) {
            try {
                if (!blob || !window.ClipboardItem || !navigator.clipboard?.write) {
                    throw new Error('Image clipboard not supported');
                }

                const item = new ClipboardItem({ 'image/png': blob });
                await navigator.clipboard.write([item]);
                setIsQrCopied(true);
                setTimeout(() => setIsQrCopied(false), 2000);
                notifications.success({
                    title: 'QR copied',
                    description: 'Participant QR code copied to the clipboard.',
                });
            } catch (error) {
                notifications.error({
                    title: 'QR copy failed',
                    description: error?.message,
                });
            }
        });
    };

    return (
        <div
            style={stylesWrapper.containerModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="share-room-title"
            aria-describedby="share-room-description"
            ref={modalRef}
            tabIndex="-1"
        >
            <div style={{ ...stylesWrapper.wrapperModal, height: 350, width: 450 }}>
                <div className="header-container">
                    <div className="header-title">
                        <h2
                            id="share-room-title"
                            style={{ margin: 0, fontSize: 'inherit' }}
                            tabIndex="0"
                        >
                            Share room
                        </h2>
                    </div>
                    <div className="header-options">
                        <button
                            className="a-element"
                            onClick={handleClose}
                            aria-label="Close share room dialog"
                            aria-describedby="close-share-tooltip"
                            tabIndex="0"
                        >
                            <Times />
                        </button>
                    </div>
                </div>
                <div
                    className="body-container"
                    style={{ ...stylesWrapper.bodyModalInvite, textAlign: 'center' }}
                >
                    <div id="share-room-description" tabIndex="0" role="region" aria-live="polite">
                        <p>Share this room with your participants</p>
                    </div>
                    <div style={{ marginTop: 20 }}>
                        <QRCodeSVG
                            value={link}
                            size={128}
                            id="qr-share-room"
                            role="img"
                            aria-label={`QR code for room invitation link: ${link}`}
                            tabIndex="0"
                        />
                        <QRCodeCanvas
                            value={link}
                            size={128}
                            id="canvas-share-room"
                            aria-hidden="true"
                            style={{ display: 'none' }}
                        />
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                            marginTop: 20,
                        }}
                    >
                        <button
                            type="button"
                            className="Button  Button-primary"
                            style={{ textTransform: 'capitalize' }}
                            onClick={copyLinkToClipboard}
                            aria-describedby="copy-link-tooltip"
                            tabIndex="0"
                        >
                            {isCopied ? 'Link Copied!' : 'Copy Link'}
                        </button>
                        <button
                            type="button"
                            className="Button  Button-secondary"
                            style={{ textTransform: 'capitalize' }}
                            onClick={() => copyQrToClipboard('canvas-share-room')}
                            aria-describedby="copy-qr-tooltip"
                            tabIndex="0"
                        >
                            {isQrCopied ? 'QR Copied!' : 'Copy QR'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const stylesWrapper = {
    fullSubtitlesContainer: {
        width: 'calc(100% - 10px)',
        height: 'calc(100% - 10px)',
        margin: '5px auto',
        position: 'relative',
        zIndex: 20,
    },
    subtitlesContainer: {
        width: 'calc(100% - 10px)',
        height: 'calc(50% - 10px)',
        margin: '5px auto',
    },
    chatContainer: {
        width: 'calc(100% - 10px)',
        height: 'calc(50% - 10px)',
        margin: '5px auto',
    },
    containerModal: {
        display: 'flex',
        justifyContent: 'center',
        height: 'calc(100vh - 50px)',
        width: '100vw',
        background: 'rgba(1,1,1,0.3)',
        position: 'absolute',
        left: 0,
        top: 0,
        zIndex: 2000,
    },
    bodyModal: {
        marginTop: 24,
        height: 'calc(100% - 90px)',
        overflow: 'auto',
    },
    bodyModalInvite: {
        marginTop: 24,
        height: 'calc(100% - 30px)',
        overflow: 'auto',
    },
    footerModal: {},
    wrapperModal: {
        padding: '10px',
        backgroundColor: 'white',
        borderRadius: 5,
        height: 500,
        top: 'calc(50% - 250px)',
        width: 320,
        position: 'absolute',
        WebkitBoxShadow: '4px 4px 9px -1px rgba(0,0,0,0.75)',
        MozBoxShadow: '4px 4px 9px -1px rgba(0,0,0,0.75)',
        boxShadow: '4px 4px 9px -1px rgba(0,0,0,0.75)',
    },
    participantItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 1em',
        cursor: 'default',
    },
    descriptionParticipant: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    fullContainer: {
        width: 'calc(100% - 20px)',
        height: '100%',
        margin: '10px auto',
    },
};

export default Main;
